import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Badge, Button, Input, HelpTooltip } from '@/kit';
import { Label } from '../../../../components/ui/label';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { Textarea } from '../../../../components/ui/textarea';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Banner } from '../../../../components/ui/Banner';
import { slugifyRole } from '../../../../lib/slugify';
import type { SeedRoleWire } from '../../../../api/types/ApplicationLocal';
import { groupByAction } from '../../../../lib/permissionOrder';

/**
 * Permiso ofrecible al sembrar un rol: la proyección mínima (key + label legible) de un permiso del
 * catálogo de la app (SYSTEM o CUSTOM). El padre la arma desde el value del PermissionCatalogStep.
 */
export interface AvailablePermission {
    permissionKey: string;
    displayName: string;
    description?: string;
    category?: string;
}

/**
 * Rol Super Admin por defecto (igual a T4 / CreateApplicationForm): toda app debe poder onboardear un
 * tenant, lo que EXIGE un seedRole con permisos de admin. Sus permisos son SYSTEM del catálogo sembrado.
 */
export const DEFAULT_SUPER_ADMIN_SEED_ROLE: SeedRoleWire = {
    roleId: 'tenant_super_admin',
    displayName: 'Super Admin',
    permissions: [
        'tenant.user.create',
        'tenant.user.read',
        'tenant.role.assign',
        'tenant.role.list',
    ],
    scope: 'TENANT',
    isSystem: true,
};

// Verbos que marcan un permiso como "de administración" (gestión de roles/usuarios). La app es
// onboard-able solo si algún seedRole los tiene (regla: al menos un rol con permisos de admin).
const ADMIN_PERMISSION_PATTERNS = ['.role.assign', '.role.create', '.user.create', '.user.delete'];

function isAdminPermission(key: string): boolean {
    return ADMIN_PERMISSION_PATTERNS.some((p) => key.includes(p));
}

/** True si al menos un rol tiene permisos de administración. El padre lo usa para gatear el submit. */
export function hasAdminRole(roles: SeedRoleWire[]): boolean {
    return roles.some((r) => r.permissions.some(isAdminPermission));
}

/** Agrupa los permisos disponibles por categoría (dominio) → acción, para presentarlos igual que el
 *  Catálogo y el PermissionPicker (reusa `groupByAction`). */
function groupPerms(perms: AvailablePermission[]) {
    const byCat = new Map<string, (AvailablePermission & { key: string })[]>();
    for (const p of perms) {
        const domain = p.permissionKey.split('.')[0];
        const cat = p.category?.trim() || (domain ? domain.toUpperCase() : 'OTROS');
        const list = byCat.get(cat) ?? [];
        list.push({ ...p, key: p.permissionKey });
        byCat.set(cat, list);
    }
    return [...byCat.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], 'es'))
        .map(([category, items]) => ({ category, groups: groupByAction(items) }));
}

/** ¿La descripción aporta algo más que repetir la key? Algunos catálogos la autogeneran como
 *  "menu <key>" (ruido); en ese caso no la mostramos. */
function isMeaningfulDesc(desc: string | undefined, key: string): boolean {
    const d = (desc ?? '').trim().toLowerCase();
    if (!d) return false;
    const k = key.toLowerCase();
    return d !== k && d !== `menu ${k}`;
}

interface Props {
    /** Roles a sembrar, controlados. */
    value: SeedRoleWire[];
    /** Se llama con la lista completa actualizada al agregar/editar/quitar un rol. */
    onChange: (next: SeedRoleWire[]) => void;
    /** Permisos disponibles (SYSTEM + CUSTOM) que ofrece el picker. Vienen del catálogo del padre. */
    availablePermissions: AvailablePermission[];
}

interface RoleDraft {
    roleId: string;
    displayName: string;
    description: string;
    permissions: string[];
    isSystem: boolean;
}

const EMPTY_DRAFT: RoleDraft = {
    roleId: '',
    displayName: '',
    description: '',
    permissions: [],
    isSystem: false,
};

function toDraft(role: SeedRoleWire): RoleDraft {
    return {
        roleId: role.roleId,
        displayName: role.displayName,
        description: role.description ?? '',
        permissions: role.permissions,
        isSystem: role.isSystem,
    };
}

/**
 * Paso CONTROLADO y PURO de roles seed del armador de aplicaciones (#3-a). No llama a la API: recibe
 * `value` + `onChange` + la lista de permisos disponibles (del PermissionCatalogStep del padre) y los
 * reporta. Incluye un permission-picker (calca el patrón de PlatformRoleForm) que elige de esa lista.
 * Muestra el displayName del rol, nunca el roleId crudo (anti-id en UI). Advierte si no hay ningún rol
 * con permisos de admin (la app no sería onboard-able); el gateo duro del submit lo hace el padre con
 * `hasAdminRole`.
 */
export function SeedRolesStep({ value, onChange, availablePermissions }: Props) {
    // null = cerrado; '' = agregando; string = editando ese roleId.
    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState<RoleDraft>(EMPTY_DRAFT);
    const [nameError, setNameError] = useState<string | undefined>(undefined);

    // Orden alfabético por displayName (default del proyecto).
    const sortedRoles = [...value].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    const permGroups = groupPerms(availablePermissions);
    const showAdminWarning = value.length > 0 && !hasAdminRole(value);

    const openAdd = (): void => {
        setDraft(EMPTY_DRAFT);
        setNameError(undefined);
        setEditing('');
    };
    const openEdit = (role: SeedRoleWire): void => {
        setDraft(toDraft(role));
        setNameError(undefined);
        setEditing(role.roleId);
    };
    const cancel = (): void => {
        setEditing(null);
        setNameError(undefined);
    };

    const togglePerm = (key: string): void => {
        setDraft((d) => ({
            ...d,
            permissions: d.permissions.includes(key)
                ? d.permissions.filter((k) => k !== key)
                : [...d.permissions, key],
        }));
    };

    const save = (): void => {
        const displayName = draft.displayName.trim();
        if (!displayName) {
            setNameError('Pon un nombre para el rol.');
            return;
        }
        const isNew = editing === '';
        // El roleId de un rol nuevo se deriva del nombre (slug, como PlatformRoleForm). Al editar, se
        // preserva el roleId original (identificador inmutable).
        const roleId = isNew ? slugifyRole(displayName) : draft.roleId;
        if (isNew && value.some((r) => r.roleId === roleId)) {
            setNameError('Ya existe un rol con ese nombre. Cambialo.');
            return;
        }
        const next: SeedRoleWire = {
            roleId,
            displayName,
            description: draft.description.trim() || undefined,
            permissions: draft.permissions,
            // El scope del rol se mantiene (TENANT por defecto en roles seed nuevos); no se pide acá.
            scope: isNew ? 'TENANT' : (value.find((r) => r.roleId === editing)?.scope ?? 'TENANT'),
            isSystem: draft.isSystem,
        };
        onChange(isNew ? [...value, next] : value.map((r) => (r.roleId === editing ? next : r)));
        setEditing(null);
    };

    const remove = (roleId: string): void => {
        onChange(value.filter((r) => r.roleId !== roleId));
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                    <Label>Roles a sembrar</Label>
                    <HelpHint text={HELP_TEXT.role} />
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={openAdd}
                >
                    Agregar rol
                </Button>
            </div>

            {showAdminWarning && (
                <Banner variant="warning">
                    Necesitas al menos un rol con permisos de admin (gestión de roles/usuarios) para
                    que la app sea onboard-able.
                </Banner>
            )}

            {sortedRoles.length === 0 && editing === null ? (
                <p className="text-xs text-muted-foreground">
                    Sin roles seed. Agrega al menos un rol con permisos de admin.
                </p>
            ) : (
                <ul className="space-y-2">
                    {sortedRoles.map((role) => (
                        <li
                            key={role.roleId}
                            data-testid={`role-row-${role.roleId}`}
                            className="flex items-start justify-between gap-2 rounded-md border px-3 py-2"
                        >
                            <div className="min-w-0 space-y-0.5">
                                <div className="flex items-center gap-2">
                                    <span
                                        data-testid="role-name"
                                        className="text-sm font-medium leading-tight"
                                    >
                                        {role.displayName}
                                    </span>
                                    {role.isSystem && (
                                        <span className="flex items-center gap-1">
                                            <Badge variant="info">Sistema</Badge>
                                            <HelpTooltip content="Rol predefinido por la aplicación (sistema) vs. creado a mano. Los de sistema no se pueden borrar en el tenant." />
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground leading-tight">
                                    {role.permissions.length} permiso(s)
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Editar rol ${role.displayName}`}
                                    onClick={() => {
                                        openEdit(role);
                                    }}
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Quitar rol ${role.displayName}`}
                                    onClick={() => {
                                        remove(role.roleId);
                                    }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing !== null && (
                <div className="space-y-3 rounded-md border bg-muted/40 p-3">
                    <div className="space-y-1">
                        <div className="flex items-center gap-1">
                            <Label htmlFor="srs-name">Nombre del rol</Label>
                            <HelpTooltip content="Identificador del rol (slug). Se autocalcula del nombre y queda inmutable." />
                        </div>
                        <Input
                            id="srs-name"
                            hint="Como se ve. Ej: Supervisor"
                            value={draft.displayName}
                            error={nameError}
                            onChange={(e) => {
                                setDraft((d) => ({ ...d, displayName: e.target.value }));
                                setNameError(undefined);
                            }}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="srs-desc">Descripción</Label>
                        <Textarea
                            id="srs-desc"
                            value={draft.description}
                            onChange={(e) => {
                                setDraft((d) => ({ ...d, description: e.target.value }));
                            }}
                        />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-1">
                            <Label>Permisos</Label>
                            <HelpHint text={HELP_TEXT.permission} />
                        </div>
                        {availablePermissions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                No hay permisos disponibles. Define permisos en el catálogo primero.
                            </p>
                        ) : (
                            <div data-testid="role-permission-picker" className="space-y-5">
                                {permGroups.map(({ category, groups }) => (
                                    <section key={category} className="space-y-3">
                                        <h4 className="text-sm font-semibold uppercase text-muted-foreground">
                                            {category}
                                        </h4>
                                        {groups.map(({ group, items }) => (
                                            <div
                                                key={group.id}
                                                className="space-y-2 border-l border-border pl-3"
                                            >
                                                <h5 className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
                                                    {group.label}
                                                </h5>
                                                <div className="grid gap-2 sm:grid-cols-2">
                                                    {items.map((p) => {
                                                        const checked = draft.permissions.includes(
                                                            p.permissionKey,
                                                        );
                                                        return (
                                                            <Label
                                                                key={p.permissionKey}
                                                                className={`flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 transition-colors ${
                                                                    checked
                                                                        ? 'bg-muted/60 ring-1 ring-inset ring-foreground/15'
                                                                        : 'hover:bg-muted/40'
                                                                }`}
                                                            >
                                                                <Checkbox
                                                                    checked={checked}
                                                                    onCheckedChange={() => {
                                                                        togglePerm(p.permissionKey);
                                                                    }}
                                                                    className="mt-0.5"
                                                                />
                                                                <span className="flex flex-col">
                                                                    <span className="text-sm font-medium leading-tight">
                                                                        {p.displayName ||
                                                                            p.permissionKey}
                                                                    </span>
                                                                    {isMeaningfulDesc(
                                                                        p.description,
                                                                        p.permissionKey,
                                                                    ) && (
                                                                        <span className="text-xs text-muted-foreground leading-tight">
                                                                            {p.description}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </Label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </section>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={cancel}>
                            Cancelar
                        </Button>
                        <Button type="button" size="sm" onClick={save}>
                            Guardar rol
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
